/**
 * Shared utilities for creating ticketable links
 */

import React from 'react';

import {
  accountURL,
  claimURL,
  customerTicketURL,
  detailSelfStorageUnitURL,
  dispatchURL,
  editLeadURL,
  logisticsDetailedLoadURL,
  orderURL,
  userURL,
  warehouseURL,
} from '@admin/config/routes';
import { Ticket__TicketableEnum } from '@admin/schema';

interface TicketableInfo {
  ticketableName: string | null | undefined;
  ticketableType: Ticket__TicketableEnum;
  ticketableID: string;
}

/**
 * Generate a link component for a ticketable based on its type
 */
export const getTicketableLink = ({
  ticketableName,
  ticketableType,
  ticketableID,
}: TicketableInfo): React.ReactElement => {
  if (!ticketableName || !ticketableType || !ticketableID) {
    return <span>{ticketableName || 'Unknown'}</span>;
  }

  const id = ticketableID.toString();

  const linkProps = {
    className: 'text-primary',
    style: { textDecoration: 'underline' } as React.CSSProperties,
    target: '_blank',
    rel: 'noopener noreferrer',
  };

  switch (ticketableType) {
    case Ticket__TicketableEnum.Account:
      return (
        <a href={accountURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    case Ticket__TicketableEnum.Claim:
      return (
        <a href={claimURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    case Ticket__TicketableEnum.CustomerTicket:
      return (
        <a href={customerTicketURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    case Ticket__TicketableEnum.Dispatch:
      return (
        <a href={dispatchURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    case Ticket__TicketableEnum.Lead:
      return (
        <a href={editLeadURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    case Ticket__TicketableEnum.LogisticsLoad:
      return (
        <a href={logisticsDetailedLoadURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    case Ticket__TicketableEnum.Order:
      return (
        <a href={orderURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    case Ticket__TicketableEnum.SelfStorageUnit:
      return (
        <a href={detailSelfStorageUnitURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    case Ticket__TicketableEnum.User:
      return (
        <a href={userURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    case Ticket__TicketableEnum.Warehouse:
      return (
        <a href={warehouseURL({ id })} {...linkProps}>
          {ticketableName}
        </a>
      );

    default:
      return <span>{ticketableName}</span>;
  }
};
